import React, { useEffect } from "react";
import { MdClose } from "react-icons/md";
import { useTheme } from "../../Utils/useTheme";

type Props = {
  open: boolean;
  onClose?: () => void;
  children: any;
  width?: number | string;
  staticMode?: boolean;
  fullScreen?: boolean;
};

export const Modal = ({
  open,
  onClose,
  children,
  width = "100%",
  staticMode = false,
  fullScreen = false,
}: Props) => {
  const { glass, border, isDark } = useTheme();

  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const isFull = fullScreen || isMobile;

  useEffect(() => {
    if (!open || staticMode) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, staticMode, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={staticMode ? undefined : onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: isDark ? "rgba(0,0,0,0.55)" : "rgba(15,23,42,0.35)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: isFull ? "stretch" : "center",
        justifyContent: isFull ? "stretch" : "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: isFull ? "100vw" : width,
          height: isFull ? "100vh" : "auto",
          maxWidth: isFull ? "100vw" : 960,
          margin: isFull ? 0 : "0 20px",
          borderRadius: isFull ? 0 : 20,
          background: glass,
          border: `1px solid ${border}`,
          padding: isFull ? "56px 24px 24px 24px" : 24,
          backdropFilter: "blur(22px)",
          boxShadow: isFull
            ? "none"
            : isDark
            ? "0 40px 120px rgba(0,0,0,0.65)"
            : "0 40px 120px rgba(15,23,42,0.25)",
          overflowY: isFull ? "auto" : "visible",
          animation: "modalIn 220ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        {isFull && (
          <button
            onClick={onClose}
            style={{
              position: "fixed",
              top: 16,
              right: 16,
              zIndex: 100000,
              width: 40,
              height: 40,
              borderRadius: 12,
              border: `1px solid ${border}`,
              background: glass,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(12px)",
            }}
          >
            <MdClose size={22} />
          </button>
        )}

        {children}
      </div>

      <style>{`
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};
